import { useEffect } from 'react'
// Use the raw Sentry SDK rather than an app-specific `appSentry` module so this component
// can be shared across apps. `captureException` is a no-op if the host app never called
// `Sentry.init`.
import * as Sentry from '@sentry/react'
import { setUserflowVisible } from 'shared/util/userflowVisibility'
import {
  WIDGET_BUTTON_COLOR as BUTTON_COLOR,
  WIDGET_BUTTON_COLOR_HOVER as BUTTON_COLOR_HOVER,
  WIDGET_BUTTON_SHADOW as BUTTON_SHADOW,
  WIDGET_BUTTON_SIZE
} from 'shared/util/supportWidgetButton'

/**
 * Lazily loads GitBook's "Docs Embed" widget for help.paintscout.com.
 *
 * Nothing is fetched until something dispatches `ps:open-docs`, so the GitBook bundle stays
 * off the critical path (same deferred approach as HubspotChat). The trigger lives in the
 * Userflow resource center — a custom action there runs:
 *
 *   window.dispatchEvent(new Event('ps:open-docs'))
 *
 * To land on a specific article instead of the widget's home:
 *
 *   window.dispatchEvent(new CustomEvent('ps:open-docs', { detail: { path: '/some/page' } }))
 *
 * To open on GitBook's AI assistant ("Ask PaintScout" in the in-house resource center, #25353):
 *
 *   window.dispatchEvent(new CustomEvent('ps:open-docs', { detail: { assistant: true } }))
 *
 * Userflow does NOT call `window.GitBook(...)` directly: the embed script isn't on the page
 * until the first dispatch, so those calls would throw.
 *
 * The open/close lifecycle mirrors HubspotChat: spinner in the corner while the bundle
 * downloads, Userflow's launcher yields the corner while the panel is up, and closing the
 * panel drops the user back on the Userflow resource center they came from.
 */

const GITBOOK_SITE_URL = 'https://help.paintscout.com'
const GITBOOK_SCRIPT_SRC = `${GITBOOK_SITE_URL}/~gitbook/embed/script.js`

// Themes the iframe contents. The app is light-only, so pin it rather than letting the embed
// follow the OS `prefers-color-scheme`. Must go in init's *third* argument (frame options) —
// it becomes a `theme` query param on the iframe URL.
const GITBOOK_FRAME_OPTIONS = { colorScheme: 'light' as const }

const state = {
  closeObserver: null as MutationObserver | null,
  // Set when closing the panel to open HubSpot chat, so the close watcher below knows not to
  // pop the Userflow resource center back open behind the chat widget.
  handingOffToChat: false
}

const CONTACT_SUPPORT_ACTION = {
  label: 'Contact Support',
  // Font Awesome slug, rendered by GitBook inside the iframe.
  icon: 'circle-question',
  // This callback runs in our document even though the button lives in the iframe —
  // GitBook's message channel proxies function references across the boundary.
  onClick: () => {
    state.handingOffToChat = true
    gitbook('close')
    // Handled by HubspotChat, which loads its own bundle on demand.
    window.dispatchEvent(new Event('ps:open-hubspot-chat'))
  }
}

/** `contactSupport` is false where no `HubspotChat` listens (painter-view), so the button is left out. */
const getGitbookSettings = (contactSupport: boolean) => ({
  // Also give the panel its own close button, alongside the launcher-as-X below.
  closeButton: true,
  // Drops the "Powered by GitBook" footer. May be plan-gated — if the footer stays, that's
  // why, not a wiring problem.
  trademark: false,
  actions: contactSupport ? [CONTACT_SUPPORT_ACTION] : []
})

const OPEN_EVENT = 'ps:open-docs'
const CLOSE_EVENT = 'ps:close-docs'

const WINDOW_EL_ID = 'gitbook-widget-window'
const STYLE_EL_ID = 'gitbook-embed-overrides'
const LOADER_EL_ID = 'gitbook-docs-loader'

// Fallbacks cover the spinner, which is on screen before GitBook's stylesheet loads. Once it
// does, these resolve to the same values, so the spinner sits exactly where the X lands.
const BUTTON_SIZE = `var(--gitbook-widget-button-height, ${WIDGET_BUTTON_SIZE}px)`
const CORNER_BOTTOM = 'var(--gitbook-widget-bottom, 1rem)'
const CORNER_RIGHT = 'var(--gitbook-widget-right, 1rem)'

function gitbook(...args: unknown[]) {
  // GitBook's loader synchronously stubs `window.GitBook` as a command queue and replays it
  // in order once the embed bundle downloads, so calls made before then aren't lost.
  const gb = (window as any).GitBook
  if (typeof gb === 'function') {
    gb(...args)
  }
}

function addStyleOverrides() {
  if (document.getElementById(STYLE_EL_ID)) {
    return
  }

  const style = document.createElement('style')
  style.id = STYLE_EL_ID
  // The doubled `:root:root` outspecifies GitBook's own `:root` block. `colorScheme` above
  // only themes the iframe contents — the launcher and panel frame are styled by GitBook's
  // stylesheet in *our* document, which flips these under `prefers-color-scheme: dark` and
  // would leave dark chrome around light content.
  style.textContent = `:root:root {
    --gitbook-widget-text-color: #656973;
    --gitbook-widget-border-color: #e5e5e5;
    --gitbook-widget-background-translucent: #ffffffe6;
    --gitbook-widget-background-translucent-hover: #fafafae6;
    --gitbook-widget-background-solid: #fff;
    /* Match HubSpot's launcher size. GitBook derives the panel's bottom offset from this, so
       the panel stays clear of the taller button on its own. */
    --gitbook-widget-button-height: ${WIDGET_BUTTON_SIZE}px;
  }
  /* While open, GitBook collapses its launcher to just the X icon. Restyle it into the same
     blue circle HubSpot uses, so the two widgets match and it reads as the spinner it
     replaces. The .open selectors outspecify GitBook's own base, :hover and
     prefers-contrast rules. */
  #gitbook-widget-button.open {
    width: ${BUTTON_SIZE};
    padding: 0;
    justify-content: center;
    border-radius: 50%;
    background-color: ${BUTTON_COLOR};
    border-color: ${BUTTON_COLOR};
    box-shadow: ${BUTTON_SHADOW};
    /* The X is a mask painted with currentColor. */
    color: #fff;
  }
  #gitbook-widget-button.open:hover,
  #gitbook-widget-button.open:focus-visible {
    background-color: ${BUTTON_COLOR_HOVER};
  }
  #gitbook-widget-button.open #gitbook-widget-button-icon {
    width: 24px;
    height: 24px;
  }`
  document.head.appendChild(style)
}

function showLoader() {
  if (document.getElementById(LOADER_EL_ID)) {
    return
  }

  const loader = document.createElement('div')
  loader.id = LOADER_EL_ID
  Object.assign(loader.style, {
    position: 'fixed',
    bottom: CORNER_BOTTOM,
    right: CORNER_RIGHT,
    width: BUTTON_SIZE,
    height: BUTTON_SIZE,
    borderRadius: '50%',
    backgroundColor: BUTTON_COLOR,
    // Just under GitBook's launcher (9999) so the real button takes over cleanly.
    zIndex: '9997',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: BUTTON_SHADOW
  })
  loader.innerHTML =
    '<div style="width:24px;height:24px;border:3px solid rgba(255,255,255,0.3);border-top-color:#fff;border-radius:50%;animation:gitbook-docs-spin 0.8s linear infinite"></div>' +
    '<style>@keyframes gitbook-docs-spin{to{transform:rotate(360deg)}}</style>'
  document.body.appendChild(loader)
}

function hideLoader() {
  document.getElementById(LOADER_EL_ID)?.remove()
}

/**
 * The panel can be dismissed three ways — the launcher X, the in-panel close button, and
 * `ps:close-docs` — and GitBook exposes no event for any of them (its internal `close` event
 * isn't reachable from `window.GitBook`). Watching the panel's own `hidden` class covers all
 * three.
 */
function watchForClose() {
  const windowEl = document.getElementById(WINDOW_EL_ID)
  if (!windowEl || state.closeObserver) {
    return
  }

  const observer = new MutationObserver(() => {
    if (!windowEl.classList.contains('hidden')) {
      return
    }

    observer.disconnect()
    state.closeObserver = null

    // Take the launcher back off the corner so it doesn't sit beside Userflow's.
    gitbook('hide')

    // Observer callbacks are microtasks, so a Contact Support click has already handed off to
    // HubSpot by now — reopening the resource center would surface it behind the chat.
    if (state.handingOffToChat) {
      state.handingOffToChat = false
      return
    }

    // Otherwise return the user to the resource center they opened the docs from.
    setUserflowVisible(true)
  })

  observer.observe(windowEl, { attributes: true, attributeFilter: ['class'] })
  state.closeObserver = observer
}

let loadPromise: Promise<void> | null = null

function loadEmbed(contactSupport: boolean) {
  if (loadPromise) {
    return loadPromise
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = GITBOOK_SCRIPT_SRC
    script.async = true

    script.onload = () => {
      addStyleOverrides()

      // The loader already called init, but only with the site URL — frame options (where
      // colorScheme lives) can't be passed through it, so reset and re-init. `unload` is a
      // no-op if the loader ever stops auto-initializing.
      gitbook('unload')
      gitbook('init', { siteURL: GITBOOK_SITE_URL }, GITBOOK_FRAME_OPTIONS)
      gitbook('configure', getGitbookSettings(contactSupport))

      // Keep the launcher off-screen until we're actually opening. GitBook appends it
      // visible, which would otherwise put a second pill next to Userflow's launcher.
      gitbook('hide')

      // `onload` fires when the *loader* runs; it appends the real embed bundle
      // asynchronously, so our commands above are still queued at this point. Wait for the
      // panel element to exist before resolving, so callers can drive it directly.
      const started = Date.now()
      const poll = setInterval(() => {
        if (document.getElementById(WINDOW_EL_ID)) {
          clearInterval(poll)
          resolve()
        } else if (Date.now() - started > 15_000) {
          clearInterval(poll)
          loadPromise = null
          reject(new Error('GitBook docs embed did not mount'))
        }
      }, 100)
    }

    script.onerror = () => {
      // Allow a retry on the next click rather than caching the failure forever.
      loadPromise = null
      script.remove()
      reject(new Error('Failed to load GitBook docs embed'))
    }

    document.head.appendChild(script)
  })

  return loadPromise
}

export interface GitBookDocsProps {
  /** Show GitBook's "Contact Support" action, which opens HubSpot chat. Only the contractor app mounts the chat. */
  contactSupport?: boolean
}

export default function GitBookDocs({ contactSupport = true }: GitBookDocsProps) {
  useEffect(() => {
    const onOpen = async (event: Event) => {
      const detail = (event as CustomEvent<{ path?: string; assistant?: boolean }>).detail
      const path = detail?.path

      // Yield the corner before anything else so the spinner doesn't land on top of
      // Userflow's launcher.
      setUserflowVisible(false)

      // Only the first open pays for the download; later ones open instantly, so a spinner
      // would just flash.
      if (!loadPromise) {
        showLoader()
      }

      try {
        await loadEmbed(contactSupport)
      } catch (error) {
        Sentry.captureException(error)
        hideLoader()
        setUserflowVisible(true)
        return
      }

      // Showing the launcher is what makes the X available: GitBook collapses it to a close
      // icon while the panel is open, and its stylesheet lifts the panel above it.
      gitbook('show')
      gitbook('open')
      if (detail?.assistant) {
        gitbook('navigateToAssistant')
      } else if (path) {
        gitbook('navigateToPage', path)
      }

      hideLoader()
      watchForClose()
    }

    const onClose = () => gitbook('close')

    window.addEventListener(OPEN_EVENT, onOpen)
    window.addEventListener(CLOSE_EVENT, onClose)

    return () => {
      window.removeEventListener(OPEN_EVENT, onOpen)
      window.removeEventListener(CLOSE_EVENT, onClose)
    }
  }, [contactSupport])

  return null
}
